/**
 * Scene 5: The Ranking Race
 * "Who Leads?"
 */

import { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TrophyIcon } from '../../icons/StoryIcons';
import type { StoryInsights } from '../../../hooks/useStoryData';

gsap.registerPlugin(ScrollTrigger);

interface Scene5Props {
  insights: StoryInsights;
  isActive: boolean;
}

// Color palette for states
const STATE_COLORS: Record<string, string> = {
  'Rivers': '#ef4444',
  'Akwa Ibom': '#f59e0b',
  'Delta': '#22c55e',
  'Lagos': '#3b82f6',
  'Kano': '#8b5cf6',
  'Bayelsa': '#ec4899',
  'Edo': '#14b8a6',
  'Kaduna': '#f97316',
  'Oyo': '#06b6d4',
  'Bauchi': '#84cc16',
};

const getStateColor = (state: string) => STATE_COLORS[state] || '#64748b';

export function Scene5RankingRace({ insights, isActive }: Scene5Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const raceContainerRef = useRef<HTMLDivElement>(null);
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const years = insights.rankingHistory.map(r => r.year);
  const currentData = insights.rankingHistory[currentYearIndex];
  
  // Find max total for scaling
  const maxTotal = Math.max(
    ...insights.rankingHistory.flatMap(r => r.rankings.map(s => s.total))
  );
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const ctx = gsap.context(() => {
      // Only set initial hidden state if not active
      if (!isActive) {
        gsap.set(titleRef.current, { opacity: 0, y: 50 });
        gsap.set(raceContainerRef.current, { opacity: 0 });
      }
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'center center',
          toggleActions: 'play none none reverse',
          onEnter: () => {
            // Start the race animation
            setTimeout(() => {
              setIsAnimating(true);
            }, 1000);
          },
          onLeaveBack: () => {
            setIsAnimating(false);
            setCurrentYearIndex(0);
          },
        },
      });
      
      tl.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      })
      .to(raceContainerRef.current, {
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.4');
    }, containerRef);
    
    return () => ctx.revert();
  }, []);
  
  // Force visibility when scene becomes active (for auto-scroll)
  useEffect(() => {
    if (isActive) {
      gsap.to(titleRef.current, { opacity: 1, y: 0, duration: 0.5 });
      gsap.to(raceContainerRef.current, { opacity: 1, duration: 0.5 });
      setTimeout(() => setIsAnimating(true), 500);
    }
  }, [isActive]);
  
  // Auto-advance years when animating
  useEffect(() => {
    if (!isAnimating || !isActive) return;
    
    const interval = setInterval(() => {
      setCurrentYearIndex(prev => {
        if (prev >= years.length - 1) {
          // Loop back to start after a pause
          setTimeout(() => setCurrentYearIndex(0), 2000);
          return prev;
        }
        return prev + 1;
      });
    }, 800);
    
    return () => clearInterval(interval);
  }, [isAnimating, isActive, years.length]);
  
  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-20"
    >
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-4xl w-full">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-8 group">
          <div className="flex items-center justify-center gap-4 mb-4">
            <TrophyIcon size={40} className="text-amber-400 transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 group-hover:drop-shadow-[0_0_12px_rgba(251,191,36,0.5)]" />
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white transition-all duration-300 group-hover:tracking-wide">
              The Ranking Race
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-surface-300">
            Watch how state rankings shift over time
          </p>
        </div>
        
        {/* Year indicator */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-4 px-6 py-3 bg-surface-800/50 backdrop-blur-sm rounded-full border border-surface-700">
            <button 
              onClick={() => setCurrentYearIndex(Math.max(0, currentYearIndex - 1))}
              className="text-surface-400 hover:text-white transition-colors"
              disabled={currentYearIndex === 0}
            >
              ◀
            </button>
            <span className="text-4xl font-display font-bold text-accent-400 tabular-nums w-20 text-center">
              {currentData?.year || years[0]}
            </span>
            <button 
              onClick={() => setCurrentYearIndex(Math.min(years.length - 1, currentYearIndex + 1))}
              className="text-surface-400 hover:text-white transition-colors"
              disabled={currentYearIndex === years.length - 1}
            >
              ▶
            </button>
          </div>
          
          {/* Play/Pause */}
          <button
            onClick={() => setIsAnimating(!isAnimating)}
            className="ml-4 px-4 py-2 bg-accent-500/20 text-accent-400 rounded-full text-sm font-medium hover:bg-accent-500/30 transition-colors"
          >
            {isAnimating ? '⏸ Pause' : '▶ Play'}
          </button>
        </div>
        
        {/* Race bars */}
        <div ref={raceContainerRef} className="space-y-3">
            {currentData?.rankings.slice(0, 10).map((item, index) => {
            const barWidth = (item.total / maxTotal) * 100;
            const color = getStateColor(item.state);
            
            return (
              <div
                key={item.state}
                className="flex items-center gap-4 group cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:-translate-x-1"
              >
                {/* Rank number */}
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 group-hover:scale-110
                  ${index < 3 ? 'bg-amber-500 text-surface-900 group-hover:shadow-lg group-hover:shadow-amber-500/50' : 'bg-surface-700 text-surface-300 group-hover:bg-surface-600'}
                `}>
                  {index + 1}
                </div>
                
                {/* State name */}
                <div className="w-28 text-right">
                  <span className="text-sm font-medium text-white truncate transition-colors duration-300 group-hover:text-accent-300">
                    {item.state}
                  </span>
                </div>
                
                {/* Bar */}
                <div className="flex-1 h-8 bg-surface-800 rounded-lg overflow-hidden transition-all duration-300 group-hover:bg-surface-700">
                  <div
                    className="h-full rounded-lg transition-all duration-500 ease-out flex items-center justify-end pr-3 group-hover:brightness-110"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: color,
                      boxShadow: `0 0 20px ${color}40`,
                    }}
                  >
                    <span className="text-xs font-bold text-white drop-shadow transition-transform duration-300 group-hover:scale-110">
                      ₦{(item.total / 1e12).toFixed(1)}T
                    </span>
                  </div>
                </div>
                
                {/* Medal for top 3 */}
                {index < 3 && (
                  <div className="text-xl transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12">
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Timeline scrubber */}
        <div className="mt-8">
          <div className="relative">
            <input
              type="range"
              min={0}
              max={years.length - 1}
              value={currentYearIndex}
              onChange={(e) => {
                setIsAnimating(false);
                setCurrentYearIndex(parseInt(e.target.value));
              }}
              className="w-full h-2 bg-surface-700 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-4
                [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-accent-500
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-webkit-slider-thumb]:shadow-glow
              "
            />
            <div className="flex justify-between mt-2 text-xs text-surface-500">
              <span>{years[0]}</span>
              <span>{years[Math.floor(years.length / 2)]}</span>
              <span>{years[years.length - 1]}</span>
            </div>
          </div>
        </div>
        
        {/* Consistent leaders */}
        <div className="mt-8 p-6 bg-surface-800/50 backdrop-blur-sm rounded-2xl border border-surface-700 transition-all duration-300 hover:bg-surface-800/70 hover:border-surface-600">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2 group cursor-default">
            <TrophyIcon size={20} className="text-amber-400 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
            <span className="transition-colors duration-300 group-hover:text-amber-300">Consistent Top 5 Leaders</span>
          </h3>
          <div className="flex flex-wrap gap-3">
            {insights.consistentLeaders.map(state => (
              <span
                key={state}
                className="px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-lg"
                style={{
                  backgroundColor: `${getStateColor(state)}20`,
                  color: getStateColor(state),
                  borderWidth: 1,
                  borderColor: `${getStateColor(state)}40`,
                }}
              >
                {state}
              </span>
            ))}
          </div>
          <p className="mt-4 text-sm text-surface-400 transition-colors duration-300 hover:text-surface-300">
            These states have consistently ranked in the top 5 for at least 80% of all years.
          </p>
        </div>
      </div>
    </section>
  );
}

export default Scene5RankingRace;

